import { Table } from "@tiptap/extension-table";

function escapeCell(text: string): string {
    return text.replace(/\|/g, "\\|").replace(/\n+/g, "<br>").trim();
}

function tableNodeToMarkdown(node: any): string {
    if (!node?.childCount) return "";

    const rows: string[][] = [];
    let maxCols = 0;

    node.forEach((row: any) => {
        const cells: string[] = [];
        row.forEach((cell: any) => {
            const raw = typeof cell?.textBetween === "function"
                ? cell.textBetween(0, cell.content.size, " ")
                : "";
            cells.push(escapeCell(raw ?? ""));
        });
        maxCols = Math.max(maxCols, cells.length);
        rows.push(cells);
    });

    if (rows.length === 0 || maxCols === 0) return "";

    const normalised = rows.map(row => {
        if (row.length >= maxCols) return row;
        return [...row, ...Array(maxCols - row.length).fill("")];
    });

    const header = normalised[0]!;
    const separator = Array(maxCols).fill("---");
    const body = normalised.slice(1);

    const toLine = (cells: string[]) => `| ${cells.join(" | ")} |`;
    const lines = [toLine(header), toLine(separator), ...body.map(toLine)];
    return lines.join("\n");
}

/**
 * Extends TipTap's table node with:
 *  - stable node ids for host-document node-level edits
 *  - markdown serializer annotation used by read_open_document:
 *      <!-- table:t-abc123 -->
 *      | ... |
 */
export const BridgeTable = Table.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            nodeId: {
                default: null,
                parseHTML: (element: HTMLElement) => element.getAttribute("data-node-id") || null,
                renderHTML: (attributes: Record<string, unknown>) => {
                    const nodeId = attributes.nodeId;
                    if (!nodeId) return {};
                    return { "data-node-id": String(nodeId) };
                }
            },
            pendingReplaceEditId: {
                default: null,
                parseHTML: () => null,
                renderHTML: () => ({})
            },
            pendingReplaceMarkdown: {
                default: null,
                parseHTML: () => null,
                renderHTML: () => ({})
            }
        };
    },

    addStorage() {
        return {
            markdown: {
                serialize(state: any, node: any) {
                    const id = node.attrs.nodeId ?? "unknown";
                    const markdown = tableNodeToMarkdown(node);
                    if (!markdown) return;
                    state.write(`<!-- table:${id} -->\n${markdown}`);
                    state.ensureNewLine();
                    state.write("\n");
                }
            }
        };
    }
});
